import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import LanguageToggle from "@/components/language-toggle";
import { REWARD_TIERS, TIER_COLORS } from "@/lib/reward-tiers";
import { ArrowRight, Sparkle, HandHeart, Gift, ShieldCheck } from "@phosphor-icons/react";

export default function Landing() {
  const { t } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();

  const goPortal = () => {
    if (user && user.role === "ambassador") nav("/app");
    else if (user && user.role === "admin") nav("/admin");
    else nav("/login");
  };

  return (
    <div className="min-h-screen bg-[#f6f2ea] text-[#0b1521] relative overflow-hidden">
      {/* Header */}
      <header className="relative z-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#0d3b66] flex items-center justify-center">
              <Sparkle size={16} weight="fill" color="#f4a261" />
            </div>
            <span className="font-serif-display text-2xl">{t.brand}</span>
          </Link>
          <nav className="flex items-center gap-3">
            <LanguageToggle />
            {user ? (
              <button data-testid="landing-portal-btn" onClick={goPortal} className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0d3b66] text-[#f4a261] text-sm font-medium hover:bg-[#071f36]">
                {t.nav.dashboard} <ArrowRight size={14} weight="bold" />
              </button>
            ) : (
              <>
                <Link data-testid="landing-login-btn" to="/login" className="hidden md:inline-block text-sm text-[#0d3b66] hover:opacity-70 px-3 py-2">{t.nav.login}</Link>
                <Link data-testid="landing-apply-btn" to="/apply" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0d3b66] text-[#f4a261] text-sm font-medium hover:bg-[#071f36]">
                  {t.nav.apply} <ArrowRight size={14} weight="bold" />
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-24 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0d3b66] mb-8">
              <span className="w-8 h-px bg-[#e07a5f]" /> {t.programme}
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] text-[#0b1521]">
              {t.landing.heroTitle}
            </h1>
            <p className="mt-8 text-lg text-[#3b4b5a] max-w-xl leading-relaxed">
              {t.landing.heroSub}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link data-testid="hero-apply-btn" to="/apply" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#0d3b66] text-[#f4a261] font-medium hover:bg-[#071f36]">
                {t.landing.applyCta} <ArrowRight size={16} weight="bold" />
              </Link>
              <Link data-testid="hero-login-btn" to="/login" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-[#0d3b66]/20 text-[#0d3b66] hover:border-[#0d3b66]">
                {t.landing.loginCta}
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 relative fade-up fade-up-2">
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-40px_rgba(13,59,102,0.35)]">
              <img
                src="https://images.unsplash.com/photo-1598013924228-f336656b163c?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
                alt="Smile ambassador"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0d3b66]/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-white/85 backdrop-blur-xl p-5 border border-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#f4a261] flex items-center justify-center">
                    <Gift size={20} weight="duotone" color="#0d3b66" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#6b7280]">Milestone 10</div>
                    <div className="font-serif-display text-2xl leading-none mt-0.5">Turkey Escape</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-6 -left-6 rounded-2xl bg-[#0d3b66] text-white p-4 shadow-xl hidden md:block">
              <div className="text-xs uppercase tracking-widest opacity-70">Next reward</div>
              <div className="font-serif-display text-xl mt-1 text-[#f4a261]">£200 Amazon Card</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-[#e6dfd2] bg-[#fbf9f4]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] text-[#e07a5f]">01 — Process</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4">{t.landing.howTitle}</h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-6">
            {t.landing.howSteps.map((s, i) => (
              <div key={i} className="rounded-2xl bg-white p-6 border border-[#e6dfd2] card-lift">
                <div className="text-[#f4a261] font-serif-display text-3xl">0{i + 1}</div>
                <h3 className="font-serif-display text-2xl mt-2">{s.t}</h3>
                <p className="text-sm text-[#3b4b5a] mt-3 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards ladder */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="grid md:grid-cols-12 items-end gap-6 mb-12">
            <div className="md:col-span-8">
              <div className="text-xs uppercase tracking-[0.2em] text-[#e07a5f]">02 — Rewards</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-4">{t.landing.rewardsTitle}</h2>
              <p className="text-[#3b4b5a] mt-4 max-w-2xl">{t.landing.rewardsSub}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-5 gap-3">
            {REWARD_TIERS.map((tier) => (
              <div
                key={tier.n}
                className="rounded-2xl p-5 border border-[#e6dfd2] bg-white card-lift relative overflow-hidden"
                style={{ minHeight: 170 }}
              >
                <div className="text-xs uppercase tracking-widest text-[#6b7280]">Friend #{tier.n}</div>
                <div className="font-serif-display text-2xl mt-2 leading-tight">{tier.type === "amazon" ? `£${tier.amount}` : tier.type === "hotel" ? "Turkey" : "£2,700"}</div>
                <div className="text-sm text-[#3b4b5a] mt-2">
                  {tier.type === "amazon" ? "Amazon Gift Card" : tier.type === "hotel" ? "5-night stay" : "Treatment Credit"}
                </div>
                <div className="absolute bottom-4 left-5">
                  <span
                    className="inline-flex items-center rounded-full text-[10px] font-medium px-2 py-0.5 tracking-widest uppercase"
                    style={{ backgroundColor: TIER_COLORS[tier.level].bg, color: TIER_COLORS[tier.level].ink }}
                  >
                    {tier.level}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ambassadors positioning */}
      <section className="border-t border-[#e6dfd2] bg-[#0d3b66] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[#f4a261]">03 — Ethics</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-4 text-white">{t.landing.ambassadorsTitle}</h2>
            <p className="mt-6 text-white/75 leading-relaxed max-w-xl">{t.landing.ambassadorsSub}</p>
            <div className="mt-8 flex gap-6">
              <div className="flex items-start gap-3">
                <HandHeart size={28} color="#f4a261" weight="duotone" />
                <div>
                  <div className="text-sm font-medium">Personal, not pushy</div>
                  <div className="text-xs text-white/60 mt-1">Just share your link.</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <ShieldCheck size={28} color="#f4a261" weight="duotone" />
                <div>
                  <div className="text-sm font-medium">Clinic-led care</div>
                  <div className="text-xs text-white/60 mt-1">All medical decisions stay with our team.</div>
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-6">
            <img
              src="https://images.unsplash.com/photo-1616391182219-e080b4d1043a?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
              alt="Clinic"
              className="rounded-[2rem] w-full h-[420px] object-cover"
            />
          </div>
        </div>
      </section>

      <footer className="border-t border-[#e6dfd2] py-10 text-center text-xs text-[#6b7280]">
        {t.landing.footer}
      </footer>
    </div>
  );
}
